import {ClassName, DefaultChildren} from '#/utils/utils';
import {ArrowUpRightIcon, XMarkIcon} from '@heroicons/react/24/solid';
import {ComponentProps, FunctionComponent} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

export const DismissableInfoTile: FunctionComponent<
  {variant?: 'regular' | 'topRightLearnMore'; onClose?: () => void} & Pick<ComponentProps<typeof Link>, 'to'> &
    DefaultChildren &
    ClassName
> = ({variant = 'regular', onClose, children, className, to}) => {
  const {t} = useTranslation('translation');

  const onClick = () => {
    onClose && onClose();
  };

  const style = {
    bgColor: 'bg-grayblue',
    borderColor: 'border-gray-500',
    textColorMain: 'text-gray-500',
    textColorLink: 'text-deepblue',
    infoIconColor: 'bg-gray-500',
    infoIconTextColor: 'text-grayblue',
    closeButtonColor: 'fill-gray-500 stroke-gray-500',
    separatorColor: 'bg-gray-500',
  };

  const LearnMore: FunctionComponent = () => {
    const {t} = useTranslation('translation');

    return (
      <Link
        target='_blank'
        to={to}
        className={`flex items-center gap-1 text-xs ${
          style.textColorLink
        } overflow-hidden whitespace-nowrap hover:opacity-70 ${variant === 'topRightLearnMore' ? 'ml-2' : ''}`}
      >
        {t('actions.learnMore')}
        <ArrowUpRightIcon className='w-2.5 h-2.5 fill-deepblue stroke-deepblue' />
      </Link>
    );
  };

  return (
    <div className={`border rounded-lg ${style.bgColor} ${style.borderColor} ${className}`}>
      <div className='h-full flex justify-between items-start w-full'>
        <div className='flex'>
          <div className='aspect-square flex-center p-2 group'>
            <span
              className={`w-3 h-3 rounded-full ${style.infoIconColor} ${style.infoIconTextColor} text-xs flex-center select-none`}
            >
              {t('symbols.info')}
            </span>
          </div>
        </div>

        <div className='grow h-full overflow-hidden flex items-center'>
          <span className={`text-xs ${style.textColorMain} py-1.5 text-clip`}>
            {children}
            {variant === 'regular' && (
              <>
                &nbsp;
                <span className='inline-block'>
                  <LearnMore />
                </span>
              </>
            )}
          </span>
        </div>

        <div className='flex'>
          {variant === 'topRightLearnMore' && (
            <>
              <LearnMore />

              <span className={`w-px my-1.5 ml-2 ${style.separatorColor} opacity-50`} />
            </>
          )}

          {onClose && (
            <button className='aspect-square flex-center p-2 group' onClick={onClick}>
              <XMarkIcon
                className={`w-3 h-3 ${style.closeButtonColor} group-hover:opacity-50 group-hover:opacity-50`}
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
